import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { GridApi } from 'ag-grid-community';
import { HelperService } from 'src/app/services/helper.service';
import {Helper} from 'src/app/core/helpers/helper';

type QuoteGridScope = 'quote' | 'rtmp' | 'rfq' | 'payment' | 'public-quote';

@Component({
  selector: 'app-quote-credit-payment-grids',
  templateUrl: './quote-credit-payment-grids.component.html',
  styleUrls: ['./quote-credit-payment-grids.component.css']
})
export class QuoteCreditPaymentGridsComponent implements OnChanges {
  @Input() quoteCredits: any[] = [];
  @Input() isRFQAfterAmendment = false;
  @Input() bcfPaymentFee = 0;
  @Input() scope: QuoteGridScope = 'rtmp';
  @Input() quoteStatus: string = '';

  @Output() deleteRequested = new EventEmitter<any>();

  creditsGridApi!: GridApi;
  paymentGridApi!: GridApi;

  defaultColDef: any = {
    flex: 1,
    resizable: true,
    floatingFilter: true,
    wrapHeaderText: true
  };

  paymentPinnedRowStyle = (params: any) => {
    if (params.node.rowPinned) {
      return { fontWeight: '700' };
    }
    return null;
  };

  creditClassColumnDefs: any[] = [];
  paymentColumnDefs: any[] = [];

  private get isQuoteScope(): boolean {
    return this.scope === 'quote';
  }

  private get showEPBCActOffset(): boolean {
    return this.scope !== 'public-quote';
  }

  private get obligationNumberHeader(): string {
    return this.scope === 'public-quote' ? 'Quote Obligation No.' : 'Obligation No.';
  }

  get showPaymentGrid(): boolean {
    if (this.scope !== 'public-quote') {
      return true;
    }

    const normalizedStatus = (this.quoteStatus || '').toString().replace(/[\s_-]/g, '').toLowerCase();
    return normalizedStatus !== 'inprogress' && normalizedStatus !== 'prog';
  }

  constructor(private helperService: HelperService) {
    this.creditClassColumnDefs = this.buildCreditClassColumnDefs();
    this.paymentColumnDefs = this.buildPaymentColumnDefs();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.scope) {
      this.creditClassColumnDefs = this.buildCreditClassColumnDefs();
      this.paymentColumnDefs = this.buildPaymentColumnDefs();
    }

    if (changes.isRFQAfterAmendment && !changes.scope) {
      this.paymentColumnDefs = this.buildPaymentColumnDefs();
    }

    if (changes.quoteCredits || changes.isRFQAfterAmendment || changes.bcfPaymentFee) {
      this.recomputePricesAndTotals();
      this.setRowDataToGrids();
    }
  }

  private setRowDataToGrids(): void {
    if (this.creditsGridApi) {
      this.creditsGridApi.setRowData(this.quoteCredits || []);
    }

    if (this.paymentGridApi) {
      this.paymentGridApi.setRowData(this.quoteCredits || []);
    }
  }

  onCreditsGridReady(params: any): void {
    this.creditsGridApi = params.api;
    this.creditsGridApi.sizeColumnsToFit();
    this.setRowDataToGrids();
  }

  onPaymentsGridReady(params: any): void {
    this.paymentGridApi = params.api;
    this.setRowDataToGrids();
    this.setPinnedRows();
  }

  onCreditsCellClicked(event: any): void {
    if (!this.isQuoteScope) return;

    if (event.event?.target?.getAttribute('data-action') === 'Delete') {
      this.deleteRequested.emit(event.data);
    }
  }

  onPaymentsCellClicked(event: any): void {
    if (!this.isQuoteScope) return;

    if (event.event?.target?.getAttribute('data-action') === 'Delete') {
      this.deleteRequested.emit(event.data);
    }
  }

  private recomputePricesAndTotals(): void {
    this.setPriceValues();
    this.setPinnedRows();
  }

  private setPinnedRows(): void {
    if (!this.paymentGridApi) return;

    if (this.isRFQAfterAmendment) {
      this.setNewColTotalRow();
    } else {
      this.setOldColTotalRow();
    }
  }

  private buildCreditClassColumnDefs(): any[] {
    const actionColumn = {
      headerName: 'Actions',
      field: 'actions',
      cellRenderer: (params: any) => {
        if (params.node.rowPinned) return '';
        return '<i class="fa fa-trash " style="cursor: pointer;" data-action="Delete"></i>';
      },
      minWidth: 100,
      maxWidth: 100,
      sortable: false,
      filter: false
    };

    const epbcActOffsetColumn = {
      headerName: 'EPBC Act offset',
      field: 'commonwealthOffset',
      sortable: true,
      filter: 'agTextColumnFilter',
      minWidth: 100,
      valueFormatter: (params: any) => params.value === true ? 'Yes' : 'No'
    };

    const columns = [
      {
        headerName: this.obligationNumberHeader, field: 'referenceNumber', sortable: true, filter: 'agTextColumnFilter', minWidth: 150
      },
      {
        headerName: 'IBRA Subregion', field: 'credit.ibraSubregion', sortable: true, filter: 'agTextColumnFilter', minWidth: 175
      },
      {
        headerName: 'Credit category', field: 'credit.creditCategory', sortable: true, filter: 'agTextColumnFilter', minWidth: 100
      },
      {
        headerName: 'Credit type ID', field: 'credit.creditTypeId', sortable: true, filter: 'agTextColumnFilter', minWidth: 100
      },
      {
        headerName: 'Offset trading group', field: 'credit.offsetTradingGroup', sortable: true, filter: 'agTextColumnFilter', minWidth: 250
      },
      {
        headerName: 'HBT status', field: 'credit.hbt', sortable: true, filter: 'agTextColumnFilter', minWidth: 50,
        valueGetter: (params: any) => Helper.hbtValueGetter({ data: params.data.credit })
      },
      ...(this.showEPBCActOffset ? [epbcActOffsetColumn] : []),
      {
        headerName: 'No. of BAM Credits', field: 'credit.totalCredits', sortable: true, filter: 'agTextColumnFilter', minWidth: 100, type: 'rightAligned'
      }
    ];

    return this.isQuoteScope ? [actionColumn, ...columns] : columns;
  }

  private buildPaymentColumnDefs(): any[] {
    return this.isRFQAfterAmendment
      ? this.buildNewQuotePaymentColDefs()
      : this.buildOldQuotePaymentColDefs();
  }

  private buildOldQuotePaymentColDefs(): any[] {
    const actionColumn = {
      headerName: 'Actions',
      field: 'actions',
      cellRenderer: (params: any) => {
        if (params.node.rowPinned) return '';
        return '<i class="fa fa-trash " style="cursor: pointer;" data-action="Delete"></i>';
      },
      minWidth: 100,
      maxWidth: 100,
      sortable: false,
      filter: false
    };

    const columns = [
      {
        headerName: this.obligationNumberHeader,
        valueGetter: (params: any) => params.data?.referenceNumber || '',
        sortable: true,
        filter: 'agTextColumnFilter',
        minWidth: 150
      },
      {
        headerName: 'No. of BAM Credits', field: 'credit.totalCredits', sortable: true, filter: 'agTextColumnFilter', minWidth: 140, type: 'rightAligned'
      },
      {
        headerName: 'Predicted price per credit (Exc.GST)',
        valueGetter: (params: any) => this.helperService.formatCurrency(params.data.price != null ? params.data.price.predictedPrice : 0),
        sortable: true,
        filter: 'agTextColumnFilter',
        minWidth: 220,
        type: 'rightAligned',
        colSpan: (params: any) => params.node.rowPinned ? 8 : 1,
        cellRenderer: (params: any) => {
          if (params.node.rowPinned) {
            if (params.node.rowIndex == 0) return 'Total (Excl GST)';
            if (params.node.rowIndex == 1) return 'GST';
            if (params.node.rowIndex == 2) return 'Total (Incl GST)';
            return '';
          }
          return params.value;
        },
        cellStyle: (params: any) => params.node.rowPinned ? { textAlign: 'right' } : null
      },
      {
        headerName: 'Risk premium per credit (Exc.GST)',
        valueGetter: (params: any) => this.helperService.formatCurrency(params.data.price != null ? params.data.price.riskPremium : 0),
        sortable: true, filter: 'agTextColumnFilter', minWidth: 210, type: 'rightAligned'
      },
      {
        headerName: 'Delivery fee per credit (Exc.GST)',
        valueGetter: (params: any) => this.helperService.formatCurrency(params.data.price != null ? params.data.price.deliveryFee : 0),
        sortable: true, filter: 'agTextColumnFilter', minWidth: 210, type: 'rightAligned'
      },
      {
        headerName: 'Monthly Indexation per credit (Exc.GST)',
        valueGetter: (params: any) => this.helperService.formatMonthlyIndexation(params.data.monthlyIndexationPerCredit != null ? params.data.monthlyIndexationPerCredit : 0),
        sortable: true, filter: 'agTextColumnFilter', minWidth: 230, type: 'rightAligned'
      },
      {
        headerName: 'No. of whole months lapsed',
        valueGetter: (params: any) => params.data.monthsLapsed != null ? params.data.monthsLapsed : 0,
        sortable: true, filter: 'agTextColumnFilter', minWidth: 170, type: 'rightAligned'
      },
      {
        headerName: 'Indexation per credit',
        valueGetter: (params: any) => params.data.monthlyIndexationPerCredit != null && params.data.monthsLapsed != null
          ? this.helperService.formatCurrency(params.data.monthlyIndexationPerCredit * params.data.monthsLapsed)
          : 0,
        sortable: true, filter: 'agTextColumnFilter', minWidth: 170, type: 'rightAligned'
      },
      {
        headerName: 'Total Indexed Predicted price',
        valueGetter: (params: any) => this.helperService.formatCurrency(params.data.price != null ? params.data.price.totalpredictedPrice : 0),
        sortable: true, filter: 'agTextColumnFilter', minWidth: 150, type: 'rightAligned'
      },
      {
        headerName: 'Total Indexed Risk premium',
        valueGetter: (params: any) => this.helperService.formatCurrency(params.data.price != null ? params.data.price.totalRiskPremium : 0),
        sortable: true, filter: 'agTextColumnFilter', minWidth: 150, type: 'rightAligned'
      },     
      {
        headerName: 'Total Obligation payment excluding fees',
        valueGetter: (params: any) => this.helperService.formatCurrency(params.data.price != null ? params.data.price.totalPriceExclFee : 0),
        sortable: true, filter: 'agTextColumnFilter', type: 'rightAligned', minWidth: 230
      },
      {
        headerName: 'Delivery fee',
        valueGetter: (params: any) => this.helperService.formatCurrency(params.data.price != null ? params.data.price.totalDeliveryFee : 0),
        sortable: true, filter: 'agTextColumnFilter', minWidth: 150, type: 'rightAligned'
      },
      {
        headerName: 'Total obligation payment',
        valueGetter: (params: any) => this.helperService.formatCurrency(params.data.price != null ? params.data.price.totalPrice : 0),
        sortable: true, filter: 'agTextColumnFilter', minWidth: 240, type: 'rightAligned'
      }
    ];

    return this.isQuoteScope ? [actionColumn, ...columns] : columns;
  }

  private buildNewQuotePaymentColDefs(): any[] {
    const actionColumn = {
      headerName: 'Actions',
      field: 'actions',
      cellRenderer: (params: any) => {
        if (params.node.rowPinned) return '';
        return '<i class="fa fa-trash " style="cursor: pointer;" data-action="Delete"></i>';
      },
      minWidth: 100,
      maxWidth: 100,
      sortable: false,
      filter: false
    };

    const columns = [
      {
        headerName: this.obligationNumberHeader,
        valueGetter: (params: any) => params.data?.referenceNumber || '',
        sortable: true,
        filter: 'agTextColumnFilter',
        minWidth: 150
      },
      {
        headerName: 'No. of BAM Credits', field: 'credit.totalCredits', sortable: true, filter: 'agTextColumnFilter', minWidth: 140, type: 'rightAligned'
      },
      {
        headerName: 'Predicted price per credit (Exc.GST)',
        valueGetter: (params: any) => this.helperService.formatCurrency(params.data.price != null ? params.data.price.predictedPrice : 0),
        sortable: true, filter: 'agTextColumnFilter', minWidth: 220, type: 'rightAligned',
        colSpan: (params: any) => params.node.rowPinned ? 6 : 1,
        cellRenderer: (params: any) => {
          if (params.node.rowPinned) {
            if (params.node.rowIndex == 0) return 'Delivery cost - Fixed fee';
            if (params.node.rowIndex == 1) return 'Total (Excl GST)';
            if (params.node.rowIndex == 2) return 'GST';
            if (params.node.rowIndex == 3) return 'Total (Incl GST)';
            return '';
          }
          return params.value;
        },
        cellStyle: (params: any) => params.node.rowPinned ? { textAlign: 'right' } : null
      },
      {
        headerName: 'Risk premium per credit (Exc.GST)',
        valueGetter: (params: any) => this.helperService.formatCurrency(params.data.price != null ? params.data.price.riskPremium : 0),
        sortable: true, filter: 'agTextColumnFilter', minWidth: 210, type: 'rightAligned'
      },
      {
        headerName: 'Delivery fee per credit (Exc.GST)',
        valueGetter: (params: any) => this.helperService.formatCurrency(params.data.price != null ? params.data.price.deliveryFee : 0),
        sortable: true, filter: 'agTextColumnFilter', minWidth: 210, type: 'rightAligned'
      },
      {
        headerName: 'External fee per credit (Exc.GST)',
        valueGetter: (params: any) => this.helperService.formatCurrency(params.data.price != null ? params.data.price.externalFeePerCredit : 0),
        sortable: true, filter: 'agTextColumnFilter', minWidth: 230, type: 'rightAligned'
      },
      {
        headerName: 'Predicted price (GST payable)',
        valueGetter: (params: any) => this.helperService.formatCurrency(params.data.price != null ? params.data.price.totalpredictedPrice : 0),
        sortable: true, filter: 'agTextColumnFilter', minWidth: 150, type: 'rightAligned'
      },
      {
        headerName: 'Risk premium (GST payable)',
        valueGetter: (params: any) => this.helperService.formatCurrency(params.data.price != null ? params.data.price.totalRiskPremium : 0),
        sortable: true, filter: 'agTextColumnFilter', minWidth: 150, type: 'rightAligned'
      },
      {
        headerName: 'Obligation payment excluding fees (GST payable)',
        valueGetter: (params: any) => {
          if (params.node?.rowPinned && params.node.rowIndex === 0) return '';
          return this.helperService.formatCurrency(params.data.price != null ? params.data.price.totalPriceExclFee : 0);
        },
        sortable: true, filter: 'agTextColumnFilter', type: 'rightAligned', minWidth: 230
      },
      {
        headerName: 'Delivery cost - Per credit and fixed fee (GST payable)',
        valueGetter: (params: any) => this.helperService.formatCurrency(params.data.price != null ? params.data.price.totalDeliveryFee : 0),
        sortable: true, filter: 'agTextColumnFilter', minWidth: 150, type: 'rightAligned'
      },
      {
        headerName: 'Delivery cost - External fee (Non-GST)',
        valueGetter: (params: any) => {
          if (params.node?.rowPinned && params.node.rowIndex === 0) return '';
          return this.helperService.formatCurrency(params.data.price != null ? params.data.price.totalExternalFee : 0);
        },
        sortable: true, filter: 'agTextColumnFilter', minWidth: 150, type: 'rightAligned'
      },
      {
        headerName: 'Total obligation payment',
        valueGetter: (params: any) => this.helperService.formatCurrency(params.data.price != null ? params.data.price.totalPrice : 0),
        sortable: true, filter: 'agTextColumnFilter', minWidth: 240, type: 'rightAligned'
      }
    ];

    return this.isQuoteScope ? [actionColumn, ...columns] : columns;
  }

  private setPriceValues(): void {
    (this.quoteCredits || []).forEach((credit: any) => {
      const totalCredits = credit?.credit?.totalCredits != null ? credit.credit.totalCredits : 0;
      credit.price = credit.price || {};

      if (this.isRFQAfterAmendment) {
        const predictedPrice = credit.price.predictedPrice != null ? credit.price.predictedPrice : 0;
        const riskPremium = credit.price.riskPremium != null ? credit.price.riskPremium : 0;
        const externalFeePerCredit = credit.price.externalFeePerCredit != null ? credit.price.externalFeePerCredit : 0;
        const deliveryFeePerCredit = credit.price.deliveryFee != null ? credit.price.deliveryFee : 0;

        credit.price.totalpredictedPrice = totalCredits * predictedPrice;
        credit.price.totalRiskPremium = totalCredits * riskPremium;
        credit.price.totalPriceExclFee = credit.price.totalpredictedPrice + credit.price.totalRiskPremium;
        credit.price.externalFeePerCredit = externalFeePerCredit;
        credit.price.deliveryFee = deliveryFeePerCredit;
        credit.price.totalExternalFee = totalCredits * externalFeePerCredit;
        credit.price.totalDeliveryFee = totalCredits * deliveryFeePerCredit;
        credit.price.totalPrice = credit.price.totalPriceExclFee + credit.price.totalExternalFee + credit.price.totalDeliveryFee;
        credit.price.bcfPaymentFee = this.bcfPaymentFee;
      } else {
        const predictedPrice = credit.price.indexedPredictedPricePerCredit != null ? credit.price.indexedPredictedPricePerCredit : 0;
        const riskPremium = credit.price.indexedRiskPremiumPaymentPerCredit != null ? credit.price.indexedRiskPremiumPaymentPerCredit : 0;
        credit.price.totalpredictedPrice = totalCredits * (predictedPrice);
        credit.price.totalRiskPremium = totalCredits * (riskPremium);
        credit.price.totalIndexation = totalCredits * (credit.monthlyIndexationPerCredit != null && credit.monthsLapsed != null ? credit.monthlyIndexationPerCredit * credit.monthsLapsed : 0);
        credit.price.totalDeliveryFee = totalCredits * (credit.price.deliveryFee != null ? credit.price.deliveryFee : 0);
        credit.price.totalPriceExclFee = credit.price.totalpredictedPrice + credit.price.totalRiskPremium ;
        credit.price.totalPrice = credit.price.totalPriceExclFee + credit.price.totalDeliveryFee;
      }
    });

  }

  private setOldColTotalRow(): void {
    const result: any[] = [];

    let totalwithoutgst = 0;
    let totalDeliveryFeeExclGst = 0;
    let totalPaymentExclFee = 0;

    (this.quoteCredits || []).forEach((credit: any) => {
      totalPaymentExclFee += credit.price != null ? credit.price.totalPriceExclFee : 0;
      totalwithoutgst += credit.price != null ? credit.price.totalPrice : 0;
      totalDeliveryFeeExclGst += credit.price != null ? (credit.price.totalDeliveryFee || 0) : 0;
    });
    
    const totalgst = Helper.roundAwayFromZero(totalwithoutgst * 0.10, 2);
    const totalwithgst = totalwithoutgst + totalgst;

    const totalPaymentExclFeeGst = Helper.roundAwayFromZero(totalPaymentExclFee * 0.10, 2);
    const totalPaymentExclFeeInclGst = totalPaymentExclFee + totalPaymentExclFeeGst;

    const totalDeliveryFeeGst = Helper.roundAwayFromZero(totalDeliveryFeeExclGst * 0.10, 2);
    const totalDeliveryFeeInclGst = totalDeliveryFeeExclGst + totalDeliveryFeeGst;


    result.push({
      price: {
        totalPrice: totalwithoutgst,
        totalPriceExclFee: totalPaymentExclFee,
        totalDeliveryFee: totalDeliveryFeeExclGst
      }
    });

    result.push({
      price: {
        totalPrice: totalgst,
        totalPriceExclFee: totalPaymentExclFeeGst,
        totalDeliveryFee: totalDeliveryFeeGst
      }
    });

    result.push({
      price: {
        totalPrice: totalwithgst,
        totalPriceExclFee: totalPaymentExclFeeInclGst,
        totalDeliveryFee: totalDeliveryFeeInclGst
      }
    });

    this.paymentGridApi.setPinnedBottomRowData(result);
  }

  private setNewColTotalRow(): void {
    const result: any[] = [];

    let totalwithoutgst = 0;
    let totalDeliveryFeeExclGst = 0;
    let totalPaymentExclFee = 0;
    let externalFeeTotal = 0;

    const fixedExternalFee = this.quoteCredits && this.quoteCredits.length > 0 && this.bcfPaymentFee != null ? this.bcfPaymentFee : 0;

    (this.quoteCredits || []).forEach((credit: any) => {
      totalPaymentExclFee += credit.price != null ? credit.price.totalPriceExclFee : 0;
      totalwithoutgst += credit.price != null ? credit.price.totalPrice : 0;
      totalDeliveryFeeExclGst += credit.price != null ? (credit.price.totalDeliveryFee || 0) : 0;
      externalFeeTotal += credit.price != null ? (credit.price.totalExternalFee || 0) : 0;
    });

    const totalExternalFeeGst = 0;
    const totalExternalFeeInclGst = externalFeeTotal + totalExternalFeeGst;

    const totalPaymentExclFeeGst = totalPaymentExclFee * 0.10;
    const totalPaymentExclFeeInclGst = totalPaymentExclFee + totalPaymentExclFeeGst;

    const totalDeliveryFeeGst = (totalDeliveryFeeExclGst + fixedExternalFee) * 0.10;
    const totalDeliveryFeeInclGst = totalDeliveryFeeExclGst + totalDeliveryFeeGst;

    result.push({
      price: {
        totalPriceExclFee: 0,
        totalDeliveryFee: fixedExternalFee,
        totalExternalFee: 0,
        totalPrice: fixedExternalFee
      }
    });

    result.push({
      price: {
        totalPriceExclFee: totalPaymentExclFee,
        totalDeliveryFee: totalDeliveryFeeExclGst + fixedExternalFee,
        totalExternalFee: externalFeeTotal,
        totalPrice: totalPaymentExclFee + totalDeliveryFeeExclGst + externalFeeTotal + fixedExternalFee
      }
    });

    result.push({
      price: {
        totalPriceExclFee: totalPaymentExclFeeGst,
        totalDeliveryFee: totalDeliveryFeeGst,
        totalExternalFee: totalExternalFeeGst,
        totalPrice: totalPaymentExclFeeGst + totalExternalFeeGst + totalDeliveryFeeGst
      }
    });

    result.push({
      price: {
        totalPriceExclFee: totalPaymentExclFeeInclGst,
        totalDeliveryFee: totalDeliveryFeeInclGst + fixedExternalFee,
        totalExternalFee: totalExternalFeeInclGst,
        totalPrice: totalPaymentExclFeeInclGst + totalDeliveryFeeInclGst + fixedExternalFee + totalExternalFeeInclGst
      }
    });

    this.paymentGridApi.setPinnedBottomRowData(result);
  }
}
